import { isExplicitlySignedOut, signOutAccount } from './accountSessions';
/**
 * Service Bid Board identity.
 *
 * Include server-verifiable credentials for browser and embedded sessions.
 * The user-id headers are compatibility hints, never proof of identity.
 */
import { telegram } from './telegram';
import { getWebSessionToken } from './webSessionAuth';
import { legacySnakeKey } from '../utils/legacyHostCompat';

const LEGACY_SIGNED_USER_STORAGE_KEY = legacySnakeKey('user_id');

export const BID_BOARD_SIGN_IN_HINT =
  'Sign in on this Service Bid Board with Google using the same email you paid with on Stripe, or your operator company email (for example name@mewbourne.com).';

export function isGuestBidUserId(userId: string | null | undefined): boolean {
  const value = String(userId || '').trim();
  return !value || /^(web-|dev-)/i.test(value);
}

function readSessionAuthUser(): { id?: string | number; username?: string; first_name?: string; email?: string } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem('tg_auth_user');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: string | number; username?: string; first_name?: string; email?: string };
    return parsed?.id ? parsed : null;
  } catch {
    return null;
  }
}

export function signedInBidUserId(): string {
  try {
    const live = telegram.getUser();
    if (live?.id && !isGuestBidUserId(String(live.id))) return String(live.id);
  } catch {
    /* host bridge missing in tests */
  }

  const sessionUser = readSessionAuthUser();
  if (sessionUser?.id && !isGuestBidUserId(String(sessionUser.id))) {
    return String(sessionUser.id);
  }

  if (typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem(LEGACY_SIGNED_USER_STORAGE_KEY);
      if (stored && !isGuestBidUserId(stored)) return stored;
    } catch { /* storage blocked */ }
    try {
      const stored = localStorage.getItem('telegram_user_id');
      if (stored && !isGuestBidUserId(stored)) return stored;
    } catch { /* storage blocked */ }
  }

  return '';
}

export function bidBoardSignedInLabel(): string {
  const sessionUser = readSessionAuthUser();
  const label = sessionUser?.email || sessionUser?.username || sessionUser?.first_name;
  if (label) return String(label);
  return signedInBidUserId() ? 'Signed in' : '';
}

export function webGuestUserId(): string {
  if (typeof window === 'undefined') return '';
  try {
    const existing = localStorage.getItem('dev-user-id') || localStorage.getItem('zia-web-user-id');
    if (existing) return existing;
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? `web-${crypto.randomUUID()}`
        : `web-${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem('dev-user-id', id);
    localStorage.setItem('zia-web-user-id', id);
    return id;
  } catch {
    return `web-${Math.random().toString(36).slice(2, 12)}`;
  }
}

export function currentBidUserId(): string {
  return signedInBidUserId() || webGuestUserId();
}

export const BID_BOARD_SIGN_OUT_EVENT = 'ziawellnav-bid-board-sign-out';
export const BID_BOARD_SESSION_EVENT = 'ziawellnav-bid-board-session';

/** Drop the Google / widget session so the board Sign in button returns. */
/** Persist a work-email operator session after the 6-digit domain code. */
export function persistOperatorEmailSession(input: {
  email: string;
  companyName?: string;
  principalId?: string;
}): string {
  const email = String(input.email || '').trim().toLowerCase();
  const principalId = String(input.principalId || '').trim()
    || (email ? `op-email-${email}` : '');
  if (!principalId) return '';
  const user = {
    id: principalId,
    email,
    first_name: String(input.companyName || '').trim() || email.split('@')[0] || 'Operator',
  };
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.setItem('tg_auth_user', JSON.stringify(user));
    } catch { /* ignore */ }
    try {
      localStorage.setItem(LEGACY_SIGNED_USER_STORAGE_KEY, principalId);
    } catch { /* ignore */ }
    try {
      window.dispatchEvent(new CustomEvent(BID_BOARD_SESSION_EVENT));
    } catch { /* ignore */ }
  }
  return principalId;
}

export function signOutBidBoard(): void {
  if (typeof window !== 'undefined') signOutAccount();
}

export function bidBoardAuthHeaders(): Record<string, string> {
  const signedId = signedInBidUserId();
  let telegramId = '';
  try {
    const live = telegram.getUser();
    if (live?.id) telegramId = String(live.id);
  } catch { /* ignore */ }

  const userId = signedId || webGuestUserId();
  const sessionUser = readSessionAuthUser();
  let username = '';
  try {
    username = String(telegram.getUser()?.username || '').trim();
  } catch { /* ignore */ }
  if (!username) {
    username = sessionUser?.username || '';
  }
  if (!username && typeof window !== 'undefined') {
    try {
      username = localStorage.getItem('dev-username') || '';
    } catch { /* ignore */ }
  }

  const webSession = getWebSessionToken();
  let initData = '';
  // An explicitly selected web account takes priority over the enclosing host.
  // A bare user-id header cannot authenticate an embedded sign-in.
  if (!webSession && !isExplicitlySignedOut()) {
    try { initData = telegram.getInitData?.() || ''; } catch { /* host unavailable */ }
  }
  const signed = Boolean(telegramId || signedId);

  return {
    'Content-Type': 'application/json',
    ...(webSession ? { 'X-ZiaWellNav-Web-Session': webSession } : {}),
    ...(initData ? { 'X-Telegram-Init-Data': initData } : {}),
    ...(signed
      ? {
          'X-Telegram-User-Id': userId,
          'X-Telegram-Username': username,
        }
      : {
          'X-Web-User-Id': userId,
          'X-Web-Username': username || 'web-guest',
          'X-Telegram-User-Id': userId,
          'X-Telegram-Username': username || 'web-guest',
        }),
  };
}
